"use client"

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

interface QuizProgressState {
  current: number
  total: number
}

interface QuizProgressContextValue extends QuizProgressState {
  setProgress: (next: QuizProgressState) => void
  /**
   * Wizard registers a back action whenever step > 0; chrome's
   * back button reads it. `null` = no back available (step 1), so
   * the chrome can hide the affordance entirely.
   */
  back: (() => void) | null
  setBack: (fn: (() => void) | null) => void
}

const QuizProgressContext = createContext<QuizProgressContextValue | null>(null)

/**
 * Lifts the wizard's step counter AND its back action into the
 * chrome layout so the full-width progress bar and the top-left
 * back arrow can render without coupling to wizard internals.
 *
 * The wizard writes via `useQuizProgress()?.setProgress(...)` and
 * `setBack(...)`; the chrome reads via `useQuizProgress()`. Provider
 * is mounted in `(quiz-chrome)/layout.tsx`. The wizard sits below
 * inside `<main>`.
 */
export function QuizProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<QuizProgressState>({
    current: 0,
    total: 0,
  })
  const [back, setBackState] = useState<(() => void) | null>(null)
  const setProgress = useCallback((next: QuizProgressState) => {
    setState((prev) =>
      prev.current === next.current && prev.total === next.total ? prev : next
    )
  }, [])
  const setBack = useCallback((fn: (() => void) | null) => {
    // Wrap in a setter callback so React doesn't invoke the function
    // (setState treats raw functions as updater callbacks).
    setBackState(() => fn)
  }, [])
  const value = useMemo<QuizProgressContextValue>(
    () => ({ ...state, setProgress, back, setBack }),
    [state, setProgress, back, setBack]
  )
  return (
    <QuizProgressContext.Provider value={value}>
      {children}
    </QuizProgressContext.Provider>
  )
}

export function useQuizProgress() {
  return useContext(QuizProgressContext)
}
